<template>
    <Component
        :is="contentComponent"
        :data="data"
    />
</template>

<script setup>
const route = useRoute()
const slugPath = computed(() => (Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug))

// Storyblok Visual Editor detection. Gated on `_storyblok` only — the bridge
// script only functions inside the editor iframe; `?preview` outside the
// editor renders drafts without it.
const inEditor = route.query._storyblok !== undefined

// No hand-rolled `&preview=1` here — the no-cache-fetch plugin appends
// `preview`/`no-cache` to every `/api/*` call when the page URL carries the
// editor signals (?preview / ?_storyblok).
// `deep` is editor-only: the bridge patches `article.content` in place and
// needs a deeply reactive payload; normal traffic keeps the cheap shallowRef.
const { data, error } = await useAsyncData(
    () => `article-${slugPath.value}`,
    () => $fetch(`/api/article?full_slug=${slugPath.value}`),
    { watch: [slugPath], deep: inEditor },
)

// A transient upstream failure (Storyblok 429/5xx bubbling out of /api/article)
// must NOT become a 404: search engines treat 404 as gone and can deindex the
// page, and during the v1.6.0 cache stampede every rate-limited page did exactly
// that. Re-surface it as 503 so crawlers and browsers retry. A genuinely missing
// story is NOT an error: the API returns 200 with { article: null } (a cached
// negative), which falls through to the 404 below.
if (error.value && error.value.statusCode !== 404) {
    throw createError({ statusCode: 503, statusMessage: 'Content temporarily unavailable', fatal: true })
}

if (error.value || !data.value?.article) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const article = computed(() => data.value?.article)

// The TOC's heading list is computed server-side in /api/article. Resolve it onto the
// story here (non-enumerable, so it stays out of the dehydrated payload) so the
// table-of-content blok can read it off `story.contentHeadings` regardless of which
// content type renders it. A raw story (no server payload) falls through to the blok's
// own derivation.
const contentHeadings = computed(() => data.value?.articleHeadings)

watch([article, contentHeadings], ([story, headingsList]) => {
    if (!story || story.contentHeadings) {
        return
    }
    Object.defineProperty(story, 'contentHeadings', {
        value: headingsList,
        configurable: true,
    })
}, { immediate: true })

// Every route resolves to exactly one of these, but static imports put all seven in
// the entry graph, so a tour page render-blocked on the weather and article
// stylesheets as well as its own.
//
// The chosen import is awaited here rather than wrapped in defineAsyncComponent:
// `<Component :is>` does not resolve an async component during SSR, so the content
// type would ship as an empty placeholder and only appear after hydration. An
// unknown component still renders nothing, as it did before.
const contentLoaders = {
    'article': () => import('~/components/content-types/article/index.vue'),
    'info-page': () => import('~/components/content-types/info-page/index.vue'),
    'page': () => import('~/components/content-types/page/index.vue'),
    'page-with-banner': () => import('~/components/content-types/page-with-banner/index.vue'),
    'package': () => import('~/components/content-types/package/index.vue'),
    'restaurant': () => import('~/components/content-types/restaurant/index.vue'),
    'tour': () => import('~/components/content-types/tour/index.vue'),
    'weather': () => import('~/components/content-types/weather/index.vue'),
}

// Awaited for SSR, then kept in sync client-side: the catch-all page instance is
// reused across navigations (`watch: [slugPath]` refetches in place), so the
// content type can change without a remount.
const contentComponent = shallowRef(null)

async function resolveContentComponent() {
    const loadContent = contentLoaders[article.value?.content?.component]
    if (!loadContent) {
        // An unknown content type renders nothing, as it did before.
        contentComponent.value = null
        return
    }
    try {
        contentComponent.value = (await loadContent()).default
    } catch {
        // A chunk fetch can fail after a deploy replaces the hashed asset
        // mid-session, or on a flaky network. Without a guard `contentComponent`
        // stays null and the page renders blank. Surface a real error page
        // instead. `showError` covers the client-side navigation path (the
        // watch below), where a thrown error would otherwise be swallowed.
        showError(createError({
            statusCode: 500,
            statusMessage: 'Failed to load page content',
            fatal: true,
        }))
    }
}

await resolveContentComponent()
watch(() => article.value?.content?.component, resolveContentComponent)

// Storyblok Visual Editor bridge: live re-render while an editor types.
onMounted(() => {
    if (!inEditor || !data.value?.article) {
        return
    }

    useStoryblokBridge(
        data.value.article.id,
        (updatedStory) => {
            // `input` fires on every keystroke with the full updated story.
            // Patch `content` in place (the payload is deeply reactive in the
            // editor, see `deep` above) so the `article` object identity is
            // preserved — content types destructure it in setup. Only
            // `content` is patched: the payload's fan-out data (author,
            // recommended*) is not part of the bridge event.
            data.value.article.content = updatedStory.content
            // The bridge patches only `content` (no `/api/article` re-fetch), so
            // `contentHeadings` and the stamped h2/h3 ids stay from the last server
            // load. The TOC then derives from the (stamped) story, not the server list.
        },
        {
            preventClicks: true,
        },
    )
})
</script>
